/**
 * starforgeInventory.ts — the player's extraction stash (EXAMPLE / mock economy).
 *
 * Mods are INDIVIDUAL items: one mod = one slot. Each item has its own rarity + buff.
 * Backpack holds up to MAX_SLOTS. Scrap is a currency; cases are un-opened crates.
 */
import { MOD_ORDER, type ModCode, type ModRarity } from './weaponMods';

const KEY = 'goopzone_starforge';
export const MAX_SLOTS = 50;
export type CaseRarity = ModRarity;

export interface ModItem { id: string; code: ModCode; rarity: ModRarity; }
export interface StarforgeInv { mods: ModItem[]; scrap: number; cases: CaseRarity[]; }

let _idc = 0;
function uid(): string { _idc += 1; return `m${Date.now().toString(36)}${_idc}`; }

function seed(): StarforgeInv {
  const mods: ModItem[] = [
    { id: uid(), code: 'D', rarity: 'common' },
    { id: uid(), code: 'D', rarity: 'common' },
    { id: uid(), code: 'D', rarity: 'rare' },
    { id: uid(), code: 'C', rarity: 'common' },
    { id: uid(), code: 'K', rarity: 'legendary' },
  ];
  return { mods, scrap: 12, cases: ['common', 'common', 'rare', 'legendary'] };
}

let _c: StarforgeInv | null = null;
function load(): StarforgeInv {
  if (_c) return _c;
  try {
    const s = localStorage.getItem(KEY);
    _c = s ? (JSON.parse(s) as StarforgeInv) : seed();
    if (!_c || !Array.isArray(_c.mods)) _c = seed(); // migrate any older format
  } catch { _c = seed(); }
  return _c!;
}
function save() { try { localStorage.setItem(KEY, JSON.stringify(load())); } catch { /* private mode */ } }

export function getInv(): StarforgeInv { return load(); }
export function slotsUsed(): number { return load().mods.length; }

// --- derived views (so the bench can still ask "do I own this / best rarity") ---
export function itemsOf(code: ModCode): ModItem[] { return load().mods.filter(m => m.code === code); }
export function countsFor(code: ModCode): Record<ModRarity, number> {
  const o: Record<ModRarity, number> = { common: 0, rare: 0, legendary: 0 };
  for (const m of itemsOf(code)) o[m.rarity] += 1;
  return o;
}
export function totalOwned(code: ModCode): number { return itemsOf(code).length; }
export function ownsMod(code: ModCode): boolean { return totalOwned(code) > 0; }
export function bestRarity(code: ModCode): ModRarity | null {
  const o = countsFor(code);
  if (o.legendary > 0) return 'legendary';
  if (o.rare > 0) return 'rare';
  if (o.common > 0) return 'common';
  return null;
}

const SCRAP_BY_RARITY: Record<CaseRarity, number> = { common: 5, rare: 12, legendary: 25 };
export const SALVAGE_VALUE: Record<ModRarity, number> = { common: 4, rare: 10, legendary: 22 };
export const UPGRADE_COST: Partial<Record<ModRarity, number>> = { common: 25, rare: 60 }; // scrap to reach NEXT tier
const NEXT_RARITY: Partial<Record<ModRarity, ModRarity>> = { common: 'rare', rare: 'legendary' };
export function nextRarity(r: ModRarity): ModRarity | null { return NEXT_RARITY[r] ?? null; }

/** Open a case → a new individual mod item at the case's rarity (or scrap if the backpack is full). */
export function openCase(index: number): { item: ModItem | null; rarity: ModRarity; scrapGained: number; full: boolean } {
  const inv = load();
  const rarity = inv.cases[index] ?? 'common';
  inv.cases.splice(index, 1);
  const code = MOD_ORDER[Math.floor(Math.random() * MOD_ORDER.length)];
  let item: ModItem | null = null;
  const full = inv.mods.length >= MAX_SLOTS;
  if (!full) { item = { id: uid(), code, rarity }; inv.mods.push(item); }
  const scrapGained = SCRAP_BY_RARITY[rarity] + (full ? 6 : 0);
  inv.scrap += scrapGained; save();
  return { item, rarity, scrapGained, full };
}

/** Trade one item in for scrap. Returns scrap gained. */
export function salvageItem(id: string): number {
  const inv = load();
  const i = inv.mods.findIndex(m => m.id === id);
  if (i < 0) return 0;
  const gain = SALVAGE_VALUE[inv.mods[i].rarity];
  inv.mods.splice(i, 1);
  inv.scrap += gain; save();
  return gain;
}

/** Spend scrap to upgrade one item to the next rarity (in place). Returns true on success. */
export function upgradeItem(id: string): boolean {
  const inv = load();
  const m = inv.mods.find(x => x.id === id);
  if (!m) return false;
  const nxt = NEXT_RARITY[m.rarity];
  const cost = UPGRADE_COST[m.rarity];
  if (!nxt || cost === undefined || inv.scrap < cost) return false;
  m.rarity = nxt; inv.scrap -= cost; save();
  return true;
}

/** Destroy specific mod items from the stash (used by the extraction death penalty —
 *  socketed mods are LOST when you die without extracting). Returns how many were removed. */
export function removeModItems(ids: string[]): number {
  if (!ids.length) return 0;
  const inv = load();
  const before = inv.mods.length;
  const kill = new Set(ids);
  inv.mods = inv.mods.filter(m => !kill.has(m.id));
  const removed = before - inv.mods.length;
  if (removed > 0) save();
  return removed;
}

export function addCase(rarity: CaseRarity) { load().cases.push(rarity); save(); }
/** Add scrap to the stash + persist (used by the Cloakzy gamble reward). */
export function addScrap(n: number) { const inv = load(); inv.scrap += Math.max(0, Math.round(n)); save(); }
export function resetInv() { _c = seed(); save(); }
